import {TIKTOK_PIXEL_ID} from '@/shared/config';

const TIKTOK_PIXEL_LIBRARY_NAME = 'ttq';
const TIKTOK_PIXEL_SCRIPT_URL =
  'https://analytics.tiktok.com/i18n/pixel/events.js';

const TIKTOK_PIXEL_METHODS: readonly TikTokPixelMethodName[] = [
  'page',
  'track',
  'identify',
  'instances',
  'debug',
  'on',
  'off',
  'once',
  'ready',
  'alias',
  'group',
  'enableCookie',
  'disableCookie',
  'holdConsent',
  'revokeConsent',
  'grantConsent',
];

type TikTokUserIdentity = {
  email?: string;
  id?: number;
};

let initialized = false;
let lastAppliedUserHash: string | undefined;

const getPixel = (): TikTokPixel | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.ttq ?? null;
};

const createDeferredMethod = (
  queue: TikTokPixelQueueCommand[],
  methodName: TikTokPixelMethodName
): TikTokPixelMethod => {
  return (...args: unknown[]) => {
    queue.push([methodName, ...args]);
  };
};

const createDeferredMethods = (
  queue: TikTokPixelQueueCommand[]
): Record<TikTokPixelMethodName, TikTokPixelMethod> => {
  return {
    page: createDeferredMethod(queue, 'page'),
    track: createDeferredMethod(queue, 'track'),
    identify: createDeferredMethod(queue, 'identify'),
    instances: createDeferredMethod(queue, 'instances'),
    debug: createDeferredMethod(queue, 'debug'),
    on: createDeferredMethod(queue, 'on'),
    off: createDeferredMethod(queue, 'off'),
    once: createDeferredMethod(queue, 'once'),
    ready: createDeferredMethod(queue, 'ready'),
    alias: createDeferredMethod(queue, 'alias'),
    group: createDeferredMethod(queue, 'group'),
    enableCookie: createDeferredMethod(queue, 'enableCookie'),
    disableCookie: createDeferredMethod(queue, 'disableCookie'),
    holdConsent: createDeferredMethod(queue, 'holdConsent'),
    revokeConsent: createDeferredMethod(queue, 'revokeConsent'),
    grantConsent: createDeferredMethod(queue, 'grantConsent'),
  };
};

const setAndDefer = (
  target: TikTokPixel,
  methodName: TikTokPixelMethodName
): void => {
  target[methodName] = createDeferredMethod(target, methodName);
};

const createPixelInstance = (): TikTokPixel => {
  const queue: TikTokPixelQueueCommand[] = [];
  const pixelInstance: TikTokPixel = Object.assign(
    queue,
    createDeferredMethods(queue),
    {
      methods: TIKTOK_PIXEL_METHODS,
      setAndDefer,
      instance: (_pixelId: string): TikTokPixel => {
        return pixelInstance;
      },
      load: (_pixelId: string, _options?: TikTokPixelLoadOptions): void => {
        return;
      },
    }
  );

  return pixelInstance;
};

const injectScript = (src: string): void => {
  const scriptAlreadyExists = Array.from(document.scripts).some((script) => {
    return script.src === src;
  });

  if (scriptAlreadyExists) {
    return;
  }

  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.async = true;
  script.src = src;

  const firstScript = document.getElementsByTagName('script')[0];
  if (firstScript?.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript);
    return;
  }

  document.head.appendChild(script);
};

const createPixelStub = (): TikTokPixel => {
  const queue: TikTokPixelQueueCommand[] = [];
  const pixel: TikTokPixel = Object.assign(
    queue,
    createDeferredMethods(queue),
    {
      methods: TIKTOK_PIXEL_METHODS,
      setAndDefer,
      instance: (pixelId: string): TikTokPixel => {
        const instances = pixel._i ?? {};
        pixel._i = instances;

        const pixelInstance = instances[pixelId] ?? createPixelInstance();
        instances[pixelId] = pixelInstance;

        for (const methodName of TIKTOK_PIXEL_METHODS) {
          pixel.setAndDefer(pixelInstance, methodName);
        }

        return pixelInstance;
      },
      load: (pixelId: string, options: TikTokPixelLoadOptions = {}): void => {
        const pixelInstance = createPixelInstance();
        pixel._i = {
          ...(pixel._i ?? {}),
          [pixelId]: pixelInstance,
        };
        pixelInstance._u = TIKTOK_PIXEL_SCRIPT_URL;

        pixel._t = {
          ...(pixel._t ?? {}),
          [pixelId]: Date.now(),
        };

        pixel._o = {
          ...(pixel._o ?? {}),
          [pixelId]: options,
        };

        injectScript(
          `${TIKTOK_PIXEL_SCRIPT_URL}?sdkid=${encodeURIComponent(
            pixelId
          )}&lib=${TIKTOK_PIXEL_LIBRARY_NAME}`
        );
      },
    }
  );

  return pixel;
};

const createSha256Hash = async (value: string): Promise<string> => {
  const encoder = new TextEncoder();
  const buffer = await crypto.subtle.digest('SHA-256', encoder.encode(value));
  const bytes = new Uint8Array(buffer);
  let hex = '';

  for (const byte of bytes) {
    hex += byte.toString(16).padStart(2, '0');
  }

  return hex;
};

const removeUndefinedProperties = (
  eventData: TikTokPixelEventProperties
): TikTokPixelEventProperties => {
  const result: TikTokPixelEventProperties = {};

  for (const [key, value] of Object.entries(eventData)) {
    if (value !== undefined) {
      result[key] = value;
    }
  }

  return result;
};

const canHashUserIdentity = (): boolean => {
  return typeof crypto !== 'undefined' && crypto.subtle !== undefined;
};

export function initPixel(): void {
  if (typeof window === 'undefined') {
    return;
  }

  if (initialized || TIKTOK_PIXEL_ID === undefined) {
    return;
  }

  if (getPixel() === null) {
    window.TiktokAnalyticsObject = TIKTOK_PIXEL_LIBRARY_NAME;
    window.ttq = createPixelStub();
  }

  try {
    window.ttq?.load(TIKTOK_PIXEL_ID);
    initialized = true;
  } catch (error) {
    console.warn('Error initializing TikTok Pixel:', error);
  }
}

// The official snippet calls `ttq.page()` right after `ttq.load()`.
// In this SPA we load the pixel once here and send page views on route changes.
export function trackPageView(): void {
  const pixel = getPixel();
  if (pixel === null) {
    return;
  }

  try {
    pixel.page();
  } catch (error) {
    console.warn('Error logging TikTok page view:', error);
  }
}

export function logEvent(
  eventName: TikTokPixelEventName,
  eventData?: TikTokPixelEventProperties,
  options?: TikTokPixelEventOptions
): void {
  const pixel = getPixel();
  if (pixel === null) {
    return;
  }

  try {
    if (!eventData) {
      if (options) {
        pixel.track(eventName, {}, options);
        return;
      }

      pixel.track(eventName);
      return;
    }

    const preparedEventData = removeUndefinedProperties(eventData);
    pixel.track(eventName, preparedEventData, options);
  } catch (error) {
    console.warn('Error logging TikTok event:', error);
  }
}

export async function identifyUser({
  email,
  id,
}: TikTokUserIdentity): Promise<void> {
  const pixel = getPixel();
  if (pixel === null) {
    return;
  }

  const normalizedEmail = email?.trim().toLowerCase();

  if (!normalizedEmail && id === undefined) {
    return;
  }

  if (!canHashUserIdentity()) {
    return;
  }

  try {
    const identityHash = await createSha256Hash(
      JSON.stringify({email: normalizedEmail, id})
    );

    if (lastAppliedUserHash === identityHash) {
      return;
    }

    const payload: TikTokPixelIdentifyPayload = {};

    if (normalizedEmail) {
      payload.email = await createSha256Hash(normalizedEmail);
    }

    if (id !== undefined) {
      payload.external_id = await createSha256Hash(String(id));
    }

    pixel.identify(payload);
    lastAppliedUserHash = identityHash;
  } catch (error) {
    console.warn('Error identifying TikTok user:', error);
  }
}
